import type {KeyEncodingScheme} from 'metaserver/static/js/encrypted_folder/crypto/hpke/hpkeSuite';
import {
  InvalidKeyFormatError,
  UnknownError,
} from 'metaserver/static/js/encrypted_folder/crypto/errors';
import {base64UrlDecode} from 'metaserver/static/js/encrypted_folder/crypto/hpke/utils';

export const KeyEncoding: KeyEncodingScheme = {
  async toPKCS8(privateKey: CryptoKey): Promise<Uint8Array<ArrayBuffer>> {
    const exportedKey = await crypto.subtle.exportKey('pkcs8', privateKey).catch((e) => {
      throw new UnknownError(e.message);
    });
    return new Uint8Array(exportedKey);
  },

  async fromPKCS8(privateKey: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
    return await crypto.subtle
      .importKey('pkcs8', privateKey, {name: 'ECDH', namedCurve: 'P-256'}, true, ['deriveBits'])
      .catch(() => {
        throw new InvalidKeyFormatError();
      });
  },

  async toDer(publicKey: CryptoKey): Promise<Uint8Array<ArrayBuffer>> {
    const exportedKey = await crypto.subtle.exportKey('spki', publicKey).catch((e) => {
      throw new UnknownError(e.message);
    });
    return new Uint8Array(exportedKey);
  },

  async fromDer(publicKey: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
    return await crypto.subtle
      .importKey('spki', publicKey, {name: 'ECDH', namedCurve: 'P-256'}, true, [])
      .catch(() => {
        throw new InvalidKeyFormatError();
      });
  },

  async encode(privateKey: CryptoKey): Promise<Uint8Array<ArrayBuffer>> {
    const jwkPrivate = await crypto.subtle.exportKey('jwk', privateKey).catch((e) => {
      throw new UnknownError(e.message);
    });

    return base64UrlDecode(jwkPrivate.d!); // d from jwk is always base64Url encoded
  },

  async toJWK(publicKey: Uint8Array<ArrayBuffer>): Promise<JsonWebKey> {
    const publicCryptoKey = await crypto.subtle.importKey(
      'raw',
      publicKey,
      {
        name: 'ECDH',
        namedCurve: 'P-256',
      },
      true,
      []
    );

    return await crypto.subtle.exportKey('jwk', publicCryptoKey);
  },
};
